import firebase from "firebase/app";
import "firebase/database";
import "firebase/firestore";
import moment from "moment-timezone";

const BONUS_RATE = 0.05;
const FIRESTORE_IN_BATCH_SIZE = 10;
const MAX_CONCURRENT_BATCHES = 4;

interface UserRecord {
  userId?: string;
  code?: string;
  master?: string | number;
  [key: string]: unknown;
}

export interface ReferralOwner {
  userId: string;
  code: string;
  master: string;
  sourceKey: string;
}

export interface ReferralDashboardRow {
  code: string;
  maskedCode: string;
  bonusPending: number;
  hasPendingBonus: boolean;
}

export interface ReferralDashboard {
  owner: ReferralOwner;
  bonusPending: number;
  referralCount: number;
  rows: ReferralDashboardRow[];
  calculatedAt: string;
  weekLabel: string;
  confirmationDate: string;
}

function firstRecord(
  snapshotValue: Record<string, UserRecord> | null
): { sourceKey: string; record: UserRecord } | null {
  if (!snapshotValue) return null;

  const sourceKey = Object.keys(snapshotValue)[0];
  if (!sourceKey) return null;

  return { sourceKey, record: snapshotValue[sourceKey] };
}

async function queryUserBy(
  field: "userId" | "code",
  value: string
): Promise<{ sourceKey: string; record: UserRecord } | null> {
  const snapshot = await firebase
    .database()
    .ref("/users")
    .orderByChild(field)
    .equalTo(value)
    .once("value");

  return firstRecord(snapshot.val());
}

function normalizeOwner(
  match: { sourceKey: string; record: UserRecord } | null
): ReferralOwner | null {
  if (!match || !match.record.userId) return null;

  return {
    userId: String(match.record.userId),
    code: match.record.code ? String(match.record.code) : "",
    master: match.record.master ? String(match.record.master) : "28",
    sourceKey: match.sourceKey
  };
}

export async function findReferralOwner(
  referralKey: string
): Promise<ReferralOwner | null> {
  const normalizedKey = String(referralKey || "").trim();
  if (!normalizedKey) return null;

  const directMatch = normalizeOwner(
    await queryUserBy("userId", normalizedKey)
  );
  if (directMatch) return directMatch;

  return normalizeOwner(await queryUserBy("code", normalizedKey));
}

function parseAmount(value: unknown): number {
  const parsed = Number(String(value ?? "0").replace(/,/g, ""));
  return Number.isFinite(parsed) ? parsed : 0;
}

export function maskReferralCode(code: string): string {
  const normalizedCode = String(code || "").trim();
  if (!normalizedCode) return "รหัสไม่พร้อม";
  if (normalizedCode.length <= 4) return `${normalizedCode.slice(0, 1)}•••`;

  const prefixLength = Math.min(3, normalizedCode.length - 2);
  return `${normalizedCode.slice(0, prefixLength)}•••${normalizedCode.slice(
    -2
  )}`;
}

type AmountsByUser = Record<string, number>;

function chunkValues(values: string[], size: number): string[][] {
  const chunks: string[][] = [];
  for (let index = 0; index < values.length; index += size) {
    chunks.push(values.slice(index, index + size));
  }
  return chunks;
}

async function runWithConcurrency<T>(
  tasks: Array<() => Promise<T>>,
  concurrency: number
): Promise<T[]> {
  const results = new Array<T>(tasks.length);
  let nextIndex = 0;

  async function worker(): Promise<void> {
    while (nextIndex < tasks.length) {
      const taskIndex = nextIndex;
      nextIndex += 1;
      results[taskIndex] = await tasks[taskIndex]();
    }
  }

  const workerCount = Math.min(concurrency, tasks.length);
  await Promise.all(Array.from({ length: workerCount }, () => worker()));
  return results;
}

async function sumPeriodForBatch(
  collectionName: "krungsri" | "history",
  userIds: string[],
  startOfPeriod: string
): Promise<AmountsByUser> {
  if (!userIds.length) return {};

  let query: firebase.firestore.Query = firebase
    .firestore()
    .collection(collectionName)
    .where("userId", "in", userIds)
    .where("createdate", ">=", startOfPeriod);

  if (collectionName === "history") {
    query = query
      .where("type", "==", "ถอน")
      .where("status", "==", "ดำเนินการแล้ว");
  }

  const snapshot = await query.get();
  const totals: AmountsByUser = {};
  snapshot.forEach(document => {
    const data = document.data();
    const userId = String(data.userId || "");
    if (!userId) return;
    totals[userId] = (totals[userId] || 0) + parseAmount(data.qty);
  });
  return totals;
}

async function sumPeriodByUsers(
  collectionName: "krungsri" | "history",
  userIds: string[],
  startOfPeriod: string
): Promise<AmountsByUser> {
  const uniqueUserIds = Array.from(new Set(userIds.filter(Boolean)));
  const batches = chunkValues(uniqueUserIds, FIRESTORE_IN_BATCH_SIZE);
  const totalsByBatch = await runWithConcurrency(
    batches.map(batch => () =>
      sumPeriodForBatch(collectionName, batch, startOfPeriod)
    ),
    MAX_CONCURRENT_BATCHES
  );

  return totalsByBatch.reduce<AmountsByUser>(
    (combined, totals) => Object.assign(combined, totals),
    {}
  );
}

function calculateReferralRow(
  user: UserRecord,
  depositsByUser: AmountsByUser,
  withdrawalsByUser: AmountsByUser
): ReferralDashboardRow {
  const userId = String(user.userId || "");
  const code = String(user.code || "");
  const deposits = depositsByUser[userId] || 0;
  const withdrawals = withdrawalsByUser[userId] || 0;
  const eligibleAmount = Math.max(deposits - withdrawals, 0);
  const bonusPending = Math.round(eligibleAmount * BONUS_RATE * 100) / 100;

  return {
    code,
    maskedCode: maskReferralCode(code),
    bonusPending,
    hasPendingBonus: bonusPending > 0
  };
}

export async function loadReferralDashboard(
  currentUserId: string
): Promise<ReferralDashboard> {
  const owner = await findReferralOwner(currentUserId);
  if (!owner) throw new Error("REFERRAL_OWNER_NOT_FOUND");

  const snapshot = await firebase
    .database()
    .ref("/users")
    .orderByChild("userFriendId")
    .equalTo(owner.userId)
    .once("value");
  const referredUsers = snapshot.val() as Record<string, UserRecord> | null;
  const bangkokNow = moment().tz("Asia/Bangkok");
  const weekStart = bangkokNow
    .clone()
    .startOf("day")
    .subtract((bangkokNow.day() + 2) % 7, "days");
  const weekEnd = weekStart.clone().add(1, "week").subtract(1, "second");
  const confirmationDate = weekStart.clone().add(2, "weeks");
  const startOfWeek = weekStart.format("YYYY-MM-DD HH:mm:ss");
  const referredUserList = referredUsers
    ? Object.keys(referredUsers).map(key => referredUsers[key])
    : [];
  const referredUserIds = referredUserList.map(user =>
    String(user.userId || "")
  );
  const [depositsByUser, withdrawalsByUser] = await Promise.all([
    sumPeriodByUsers("krungsri", referredUserIds, startOfWeek),
    sumPeriodByUsers("history", referredUserIds, startOfWeek)
  ]);
  const rows = referredUserList
    .map(user => calculateReferralRow(user, depositsByUser, withdrawalsByUser))
    .sort((left, right) => {
      if (left.hasPendingBonus !== right.hasPendingBonus) {
        return left.hasPendingBonus ? -1 : 1;
      }
      return left.maskedCode.localeCompare(right.maskedCode);
    });
  const bonusPending =
    Math.round(
      rows.reduce((sum, item) => sum + item.bonusPending, 0) * 100
    ) / 100;

  return {
    owner,
    bonusPending,
    referralCount: rows.length,
    rows,
    calculatedAt: bangkokNow.format("HH:mm"),
    weekLabel: `${weekStart.format("DD/MM")}–${weekEnd.format("DD/MM")}`,
    confirmationDate: confirmationDate.format("DD/MM/YYYY")
  };
}
